import { useCallback, useEffect, useState } from 'react';
import { App, Button, Input, Space, Switch, Typography, Upload } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import {
  DeploymentRecord, OemSlot, deployErrorOf, removeDeploymentOem, updateDeployment, uploadDeploymentOem,
} from '../../api/deploy';

// ============================================================
// OEM 编辑器（DW-P1-6）：开关 + 品牌标题 + 三槽位上传统一入口
// 导航图标（页签/侧栏）/ 加载 LOGO / 登录背景图；素材随快照收集（部署包/EXE 离线可用）
// ============================================================

export interface OemState {
  enabled?: boolean;
  title?: string;
  navIcon?: string | null;
  logo?: string | null;
  loginBg?: string | null;
}

const SLOTS: { slot: OemSlot; label: string; hint: string }[] = [
  { slot: 'navIcon', label: '替换导航栏图标', hint: '页签/侧栏图标（建议 64×64 PNG）' },
  { slot: 'logo', label: '替换项目加载 LOGO', hint: '加载页品牌图（建议透明 PNG）' },
  { slot: 'loginBg', label: '替换登录背景图', hint: '登录/激活页背景（建议 1920×1080）' },
];

export default function OemEditor({
  ensureDeployment,
}: {
  ensureDeployment: () => Promise<DeploymentRecord>;
}) {
  const { message } = App.useApp();
  const [oem, setOem] = useState<OemState>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<OemSlot | null>(null);

  useEffect(() => {
    ensureDeployment()
      .then((d) => setOem((d.oem ?? {}) as OemState))
      .catch(() => {});
  }, [ensureDeployment]);

  const persist = useCallback(
    async (patch: OemState) => {
      setSaving(true);
      try {
        const d = await ensureDeployment();
        const rec = await updateDeployment(d.id, { oem: patch as Record<string, unknown> });
        setOem((rec.oem ?? {}) as OemState);
      } catch (e) {
        message.error(deployErrorOf(e).message);
      } finally {
        setSaving(false);
      }
    },
    [ensureDeployment, message],
  );

  const doUpload = useCallback(
    async (slot: OemSlot, file: File) => {
      setUploading(slot);
      try {
        const d = await ensureDeployment();
        const rec = await uploadDeploymentOem(d.id, slot, file);
        setOem((rec.oem ?? {}) as OemState);
        message.success('品牌素材已上传');
      } catch (e) {
        message.error(deployErrorOf(e).message);
      } finally {
        setUploading(null);
      }
    },
    [ensureDeployment, message],
  );

  const doRemove = useCallback(
    async (slot: OemSlot) => {
      try {
        const d = await ensureDeployment();
        const rec = await removeDeploymentOem(d.id, slot);
        setOem((rec.oem ?? {}) as OemState);
        message.success('已移除');
      } catch (e) {
        message.error(deployErrorOf(e).message);
      }
    },
    [ensureDeployment, message],
  );

  return (
    <div data-testid="deploy-oem-editor">
      <Space size="large" wrap style={{ marginBottom: 10 }}>
        <Space>
          <Switch
            checked={!!oem.enabled}
            loading={saving}
            onChange={(v) => void persist({ enabled: v })}
            checkedChildren="开启项目 OEM"
            unCheckedChildren="关闭项目 OEM"
            data-testid="deploy-oem-enabled"
          />
        </Space>
        <Input
          style={{ width: 320 }}
          placeholder="品牌标题（如：XX集团数字孪生平台）"
          defaultValue={oem.title ?? ''}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== (oem.title ?? '')) void persist({ title: v });
          }}
          data-testid="deploy-oem-title"
        />
      </Space>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {SLOTS.map(({ slot, label, hint }) => {
          const url = oem[slot];
          return (
            <div
              key={slot}
              style={{ border: '1px dashed #3a3a5c', borderRadius: 8, padding: 10, textAlign: 'center' }}
              data-testid={`deploy-oem-slot-${slot}`}
            >
              <div
                style={{
                  height: 80,
                  borderRadius: 6,
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: url ? `#0d0d18 url(${url}) center/contain no-repeat` : '#0d0d18',
                  color: '#666',
                  fontSize: 12,
                }}
              >
                {url ? null : '暂无素材'}
              </div>
              <div style={{ fontSize: 13, marginBottom: 2 }}>{label}</div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {hint}
              </Typography.Text>
              <div style={{ marginTop: 8 }}>
                <Space size={4}>
                  <Upload
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      void doUpload(slot, file as unknown as File);
                      return false;
                    }}
                  >
                    <Button size="small" icon={<UploadOutlined />} loading={uploading === slot} data-testid={`deploy-oem-upload-${slot}`}>
                      上传
                    </Button>
                  </Upload>
                  {url ? (
                    <Button size="small" icon={<DeleteOutlined />} onClick={() => void doRemove(slot)}>
                      移除
                    </Button>
                  ) : null}
                </Space>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
        素材随发布快照收集：Web 链接 / 部署包 / EXE 离线均生效（页签图标、品牌标题、登录与激活页背景）。
      </div>
    </div>
  );
}
